import React, { useMemo } from 'react';
import { Box, Button, Flex } from 'rebass';
import classNames from 'classnames';
import { FiClock, FiGlobe, FiLock, FiPrinter } from 'react-icons/fi';
import { countryCodeToFlag, countryCodeToName } from 'utils/countryDisplay';
import { Label, Switch } from '@rebass/forms';

import moment from 'utils/moment';

import styles from './styles.module.css';
import { DATE_STYLES, getMomentFormat } from 'utils/dateFormats';

/**
 * Display badge with information about the respondent. For example, IP address or answered at.
 *
 * @param {React.ReactNode} icon
 * @param {string} prefix
 * @param {string} value
 */
const RespondentBadge = ({ icon, prefix, value }) => (
    <Box className={styles.badge}>
        {icon}
        <span className={styles.badgePrefix}>{prefix}:</span>
        {value}
    </Box>
);

/**
 * Display admin tools for the screener page.
 *
 * @param {boolean} [isPrintable] Indicates if the page is in printable mode
 * @param {string} [ip] IP address of the respondent
 * @param {Date | string | number} [answeredAt] Date and time the screener was answered
 * @param {string} [timezone] Timezone of the current user
 * @param {string} [countryCode] Two-letter country code from ipinfo
 * @param {React.CSSProperties} [style]
 * @param {string} [className]
 * @param {(next: boolean) => void} [onPrintableSwitchChange] Callback to switch between printable and default view.
 * @param {boolean} [isLogicVisible] Indicates if the logic switch is visible
 * @param {(value: boolean) => void} [logicSwitchOnChange] Callback to switch between logic enabled and disabled
 */
export function ScreenerAdminTools({
    className,
    style,
    isPrintable,
    onPrintableSwitchChange,
    ip,
    answeredAt,
    timezone,
    countryCode,
    isLogicVisible,
    logicSwitchOnChange
}) {
    const isRespondentInfo = !!(ip || answeredAt);
    const answeredAtValue = useMemo(() => {
        let value = moment(answeredAt);

        if (timezone) {
            value = value.tz(timezone);
        }

        return value.format(getMomentFormat(DATE_STYLES.HUMAN_DATE_TIME_SECONDS_TZ));
    }, [answeredAt, timezone]);

    return (
        <Box style={style} className={classNames('no-print', styles.container, className)}>
            <div className={styles.lock}>
                <FiLock />
                Only visible to you
            </div>
            <Flex alignItems="center" className="gap-4" flexWrap="wrap" style={{ marginBottom: '8px' }}>
                <h1 className="fs-headline-20 m-0 grow">Admin tools</h1>
                {isPrintable && (
                    <Button type="button" variant="secondary-gray" onClick={window.print} className="m-0">
                        <FiPrinter /> Print
                    </Button>
                )}
            </Flex>
            <Box style={{ marginBottom: isRespondentInfo ? '16px' : 0 }}>
                {logicSwitchOnChange && (
                    <Label htmlFor="show-all-logic-toggle" className="flex items-center gap-2 !mb-3">
                        <Switch
                            id="show-all-logic-toggle"
                            className="switch-small"
                            onClick={() => logicSwitchOnChange(!isLogicVisible)}
                            checked={isLogicVisible}
                        />
                        <span className="color-text-secondary fs-accent-12">Show all logic</span>
                    </Label>
                )}
                {onPrintableSwitchChange && (
                    <Label htmlFor="view-all-toggle" className="flex items-center gap-2">
                        <Switch
                            id="view-all-toggle"
                            className="switch-small"
                            onClick={() => onPrintableSwitchChange(!isPrintable)}
                            checked={isPrintable}
                        />
                        <span className="color-text-secondary fs-accent-12">View all pages / Print</span>
                    </Label>
                )}
            </Box>
            {isRespondentInfo && (
                <>
                    <hr className="my-4" />
                    <Box className="grow">
                        <p className="m-0 fs-accent-12 color-text-secondary" style={{ marginBottom: '8px' }}>
                            Respondent Information
                        </p>
                        <Flex className="gap-2" flexWrap="wrap">
                            {ip && <RespondentBadge icon={<FiGlobe />} prefix="IP Address" value={ip} />}
                            <RespondentBadge
                                icon={
                                    <span role="img" aria-label={countryCodeToName(countryCode)}>
                                        {countryCodeToFlag(countryCode)}
                                    </span>
                                }
                                prefix="Country"
                                value={countryCodeToName(countryCode)}
                            />
                            {answeredAt && (
                                <RespondentBadge icon={<FiClock />} prefix="Answered at" value={answeredAtValue} />
                            )}
                        </Flex>
                    </Box>
                </>
            )}
        </Box>
    );
}
